import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import type { HeaderAction } from '@/card/record'
import { ApiError } from '@/lib/api'
import { fetchTelephonyStatus, placeCall, type CallTarget, type PlacedCall } from './api'

/**
 * «Позвонить» на карточке (тикет #122).
 *
 * Карточка получает кнопку и строку итога одним хуком — как «Написать письмо» (#117): четыре
 * карточки (контакт, контрагент, лид, сделка) не повторяют логику и не расходятся в том, что
 * отправляют. Номер в запрос не кладётся — сервер берёт его с карточки, которую вызывающий видит.
 *
 * Кнопка звонит через АТС, только когда телефония подключена и у сотрудника есть добавочный.
 * Иначе — прежняя ссылка `tel:` (если номер на карточке есть): она набирает с телефона
 * пользователя и в ленте следа не оставляет, поэтому подпись у неё та же, а поведение честно
 * другое. Итог звонка через АТС называется словами: «звонит ваш телефон», отказ АТС с причиной
 * или «АТС не ответила — наберите вручную». Молча закрывшаяся кнопка читалась бы как «звоню».
 */
export function useCallAction(
  target: CallTarget,
  fallbackPhone: string | null | undefined,
): { action: HeaderAction | null; outcome: ReactNode } {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const allowed = can(user, 'sales.activity', 'create')
  const status = useQuery({
    queryKey: ['telephony-status'],
    queryFn: ({ signal }) => fetchTelephonyStatus(signal),
    enabled: allowed,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const [result, setResult] = useState<PlacedCall | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  // One key per press: a lost answer replayed by the client is the same call, a new press is a
  // new one. Kept in a ref so the retry inside one press reuses it.
  const key = useRef<string>('')

  const call = useMutation({
    mutationFn: () => placeCall(target, key.current),
    onSuccess: (placed) => {
      setFailure(null)
      setResult(placed)
      void queryClient.invalidateQueries({ queryKey: ['timeline', target.type, target.id] })
      void queryClient.invalidateQueries({ queryKey: ['activities', target.type, target.id] })
    },
    onError: (error) => {
      setResult(null)
      setFailure(error instanceof ApiError ? error.message : t('app.error'))
    },
  })

  let action: HeaderAction | null = null
  if (allowed && status.data?.mayCall) {
    action = {
      id: 'call',
      label: call.isPending ? t('telephony.call.pending') : t('telephony.call'),
      icon: 'phone',
      disabled: call.isPending,
      onClick: () => {
        key.current = newKey()
        setResult(null)
        setFailure(null)
        call.mutate()
      },
    }
  } else if (allowed && status.isLoading) {
    // Not yet known whether the PBX is connected: a tel: link pressed now would dial from the
    // person's own phone and leave no trace, when a moment later the PBX would have.
    action = { id: 'call', label: t('telephony.call'), icon: 'phone', disabled: true, onClick: () => {} }
  } else if (fallbackPhone) {
    const phone = fallbackPhone
    action = { id: 'call', label: t('telephony.call'), icon: 'phone', onClick: () => window.location.assign(`tel:${phone}`) }
  }

  const outcome = failure ? (
    <div className="alert alert-danger" role="alert">
      <Icon name="alert" />
      <span>{failure}</span>
    </div>
  ) : result ? (
    <div className={`alert ${result.status === 'ordered' ? 'alert-info' : 'alert-warning'}`} role="status">
      <Icon name="phone" />
      <span>
        {result.status === 'ordered'
          ? t('telephony.call.ordered', { number: result.number ?? '' })
          : result.status === 'rejected'
            ? t('telephony.call.rejected', { reason: result.message ?? '' })
            : t('telephony.call.unavailable')}
      </span>
    </div>
  ) : null

  return { action, outcome }
}

const newKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
